import React, { useEffect, useState } from 'react';

import { SxProps } from '@mui/material/styles';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';

import {
    useDebriefApp,
    useCurrentDebrief,
    useAnalytics,
    useCurrentTracklog,
} from 'ff/debrief/hooks';
import Theme from 'ff/debrief/common/StyledComponentThemes';
import { ApproachOverride, Segment } from 'ff/debrief/models';

const findSelectedApproach = (segment: Segment) => {
    const selected = segment?.instrumentApproaches?.find(
        iap => iap.iapSelected === true
    );
    return selected?.iapName ?? '';
};

const ApproachSelector = (props: React.PropsWithChildren<{ sx?: SxProps }>) => {
    const { selectedSegment } = useDebriefApp();
    const { isOwnTracklog } = useCurrentTracklog();
    const { saveApproachOverride } = useCurrentDebrief();
    const { logIAPChanged } = useAnalytics();

    const [approach, setApproach] = useState(() =>
        findSelectedApproach(selectedSegment)
    );

    useEffect(() => {
        const currentSelectedApproachName =
            findSelectedApproach(selectedSegment);
        setApproach(currentSelectedApproachName);
    }, [selectedSegment]);

    const createApproachOverride = (approach: string): ApproachOverride => ({
        startEpochSecond: selectedSegment.startUtc,
        duration: selectedSegment.durationInSeconds,
        approach: approach,
    });

    const handleChange = (e: SelectChangeEvent<string>) => {
        const newApproachName = e.target.value;
        const isVisualApproach = newApproachName
            .toLowerCase()
            .includes('visual');

        logIAPChanged({
            previousApproach: approach,
            newApproach: newApproachName,
            isVisualApproach,
        });

        setApproach(newApproachName);
        saveApproachOverride(createApproachOverride(newApproachName));
    };

    const hasAlternateApproaches =
        !!selectedSegment?.instrumentApproaches?.length;

    if (!hasAlternateApproaches) return null;

    return (
        <FormControl fullWidth variant="standard">
            {props.children}
            <Select
                value={approach}
                onChange={handleChange}
                sx={{
                    color: Theme.blue,
                    fontWeight: 'bold',
                    ...props.sx,
                }}
                disabled={!isOwnTracklog}
            >
                {selectedSegment.instrumentApproaches
                    .slice()
                    .reverse()
                    .map(approach => (
                        <MenuItem
                            key={approach.iapIndex}
                            value={approach.iapName}
                        >
                            {approach.iapName}
                        </MenuItem>
                    ))}
            </Select>
        </FormControl>
    );
};

export default ApproachSelector;
